import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  getAccessUsers,
  loadMoreAccessUsers,
  getFilterUsers,
  loadMoreFilterUsers,
  getProjects,
  createResonanceProject,
  uploadProjectFile,
  getProject,
  changeProjectSharing,
  changeSonarProject,
  changeSharePassword,
  changeProjectAccessUsers,
  changeProjectFile,
  createScoutProject,
  changeProject,
  getAccessAssistants,
  changeScoutProjectAccessAssistants,
  changeGenieProjectAccessAssistants,
  deactivateProject,
  changeFinalMessagesPassword,
  createId8Project,
  createGenieGoProject,
  createGenieProject,
  createActiv8Project,
} from './projectsActions';
import { ASSISTANTS_CATEGORY, EMPTY_DROPDOWN_OPTION, SCOUT_ASSISTANTS_CATEGORY } from '../../constants';

const initialState = {
  isAccessUsersLoading: false,
  isFilterUsersLoading: false,
  filterUsers: [],
  accessUsers: [],
  totalAccessUsers: 0,
  totalFilterUsers: 0,

  isLoading: false,
  projects: [],
  totalElements: 0,
  tableSettings: {
    page: 0,
    size: 12,
    sort: '-createdOn',
    totalElements: 0,
    createdBy: EMPTY_DROPDOWN_OPTION,
    projectType: '',
    searchQuery: '',
  },

  editProject: undefined,
  isNameSectionLoading: false,
  isFileSectionLoading: false,
  isShareSectionLoading: false,
  isUsersSectionLoading: false,
  isAssistantsSectionLoading: false,

  isAccessAssistantsLoading: false,
  accessAssistants: {
    [ASSISTANTS_CATEGORY.GENERATE_ALL]: [],
    [ASSISTANTS_CATEGORY.GENERATE_SELECTED]: [],
    [ASSISTANTS_CATEGORY.COMPRESS]: [],
  },
  totalAssistantsUsers: 0,
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    changeTablePage: (state, action) => {
      state.tableSettings.page = action.payload;
    },
    changeTableSize: (state, action) => {
      state.tableSettings.size = action.payload;
      state.tableSettings.page = 0;
    },
    changeTableSort: (state, action) => {
      state.tableSettings.sort = action.payload;
      state.tableSettings.page = 0;
    },
    changeTableCreatedBy: (state, action) => {
      state.tableSettings.createdBy = action.payload;
    },
    changeTableProjectType: (state, action) => {
      state.tableSettings.projectType = action.payload;
    },
    changeTableSearch: (state, action) => {
      state.tableSettings.searchQuery = action.payload;
    },
    resetEditProjectPage: (state) => {
      state.editProject = undefined;
      state.isNameSectionLoading = false;
      state.isFileSectionLoading = false;
      state.isShareSectionLoading = false;
      state.isUsersSectionLoading = false;
    },
    resetParams: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projects = action.payload.content;
        state.totalElements = action.payload.total;
      })
      .addCase(getAccessUsers.fulfilled, (state, action) => {
        state.isAccessUsersLoading = false;
        state.accessUsers = action.payload.content;
        state.totalAccessUsers = action.payload.total;
      })
      .addCase(loadMoreAccessUsers.fulfilled, (state, action) => {
        state.isAccessUsersLoading = false;
        state.accessUsers = [...state.accessUsers, ...action.payload.content];
        state.totalAccessUsers = action.payload.total;
      })
      .addCase(getAccessAssistants.fulfilled, (state, action) => {
        state.isAccessAssistantsLoading = false;
        state.accessAssistants = SCOUT_ASSISTANTS_CATEGORY.reduce((acc, assistantCategory) => {
          const assistantsInCategory = action.payload[assistantCategory];

          return {
            ...acc,
            [assistantCategory]: assistantsInCategory.map((assistant) => ({
              ...assistant,
              value: assistant.id,
              label: assistant.name,
            })),
          };
        }, {});
      })

      .addCase(getFilterUsers.fulfilled, (state, action) => {
        state.isFilterUsersLoading = false;
        state.filterUsers = action.payload.content;
        state.totalFilterUsers = action.payload.total;
      })
      .addCase(loadMoreFilterUsers.fulfilled, (state, action) => {
        state.isFilterUsersLoading = false;
        state.filterUsers = [...state.filterUsers, ...action.payload.content];
        state.totalFilterUsers = action.payload.total;
      })
      .addCase(changeProjectFile.pending, (state) => {
        state.isFileSectionLoading = true;
      })
      .addCase(changeProjectFile.fulfilled, (state) => {
        state.isFileSectionLoading = false;
      })
      .addCase(changeProjectFile.rejected, (state) => {
        state.isFileSectionLoading = false;
      })

      .addCase(changeSharePassword.fulfilled, (state) => {
        state.isShareSectionLoading = false;
      })
      .addCase(changeSharePassword.pending, (state) => {
        state.isShareSectionLoading = true;
      })
      .addCase(changeSharePassword.rejected, (state) => {
        state.isShareSectionLoading = false;
      })

      .addCase(changeFinalMessagesPassword.fulfilled, (state) => {
        state.isShareSectionLoading = false;
      })
      .addCase(changeFinalMessagesPassword.pending, (state) => {
        state.isShareSectionLoading = true;
      })
      .addCase(changeFinalMessagesPassword.rejected, (state) => {
        state.isShareSectionLoading = false;
      })

      .addCase(changeProjectAccessUsers.fulfilled, (state) => {
        state.isUsersSectionLoading = false;
      })
      .addCase(changeProjectAccessUsers.pending, (state) => {
        state.isUsersSectionLoading = true;
      })
      .addCase(changeProjectAccessUsers.rejected, (state) => {
        state.isUsersSectionLoading = false;
      })

      .addCase(changeScoutProjectAccessAssistants.fulfilled, (state) => {
        state.isAssistantsSectionLoading = false;
      })
      .addCase(changeScoutProjectAccessAssistants.pending, (state) => {
        state.isAssistantsSectionLoading = true;
      })
      .addCase(changeScoutProjectAccessAssistants.rejected, (state) => {
        state.isAssistantsSectionLoading = false;
      })

      .addCase(changeGenieProjectAccessAssistants.fulfilled, (state) => {
        state.isAssistantsSectionLoading = false;
      })
      .addCase(changeGenieProjectAccessAssistants.pending, (state) => {
        state.isAssistantsSectionLoading = true;
      })
      .addCase(changeGenieProjectAccessAssistants.rejected, (state) => {
        state.isAssistantsSectionLoading = false;
      })

      .addCase(deactivateProject.fulfilled, (state, action) => {
        const updatedProject = action.payload;
        state.projects = state.projects.map((project) => (project.id === updatedProject.id ? action.payload : project));
      })

      .addMatcher(
        isAnyOf(
          getProject.fulfilled,
          changeProjectSharing.fulfilled,
          changeSonarProject.fulfilled,
          changeSharePassword.fulfilled,
          changeFinalMessagesPassword.fulfilled,
          changeProjectAccessUsers.fulfilled,
          changeScoutProjectAccessAssistants.fulfilled,
          changeGenieProjectAccessAssistants.fulfilled,
        ),
        (state, action) => {
          state.editProject = action.payload;
        },
      )
      .addMatcher(isAnyOf(changeSonarProject.pending, changeProject.pending), (state) => {
        state.isNameSectionLoading = true;
      })
      .addMatcher(isAnyOf(changeSonarProject.fulfilled, changeProject.fulfilled), (state, action) => {
        state.isNameSectionLoading = false;
        state.editProject = action.payload;
      })
      .addMatcher(isAnyOf(changeSonarProject.rejected, changeProject.rejected), (state) => {
        state.isNameSectionLoading = false;
      })
      .addMatcher(isAnyOf(getAccessUsers.pending, loadMoreAccessUsers.pending), (state) => {
        state.isAccessUsersLoading = true;
      })
      .addMatcher(isAnyOf(getAccessUsers.rejected, loadMoreAccessUsers.rejected), (state) => {
        state.isAccessUsersLoading = false;
      })
      .addMatcher(isAnyOf(getAccessAssistants.pending), (state) => {
        state.isAccessAssistantsLoading = true;
      })
      .addMatcher(isAnyOf(getAccessAssistants.rejected), (state) => {
        state.isAccessAssistantsLoading = false;
      })
      .addMatcher(isAnyOf(getFilterUsers.pending, loadMoreFilterUsers.pending), (state) => {
        state.isFilterUsersLoading = true;
      })
      .addMatcher(isAnyOf(getFilterUsers.rejected, loadMoreFilterUsers.rejected), (state) => {
        state.isFilterUsersLoading = false;
      })
      .addMatcher(isAnyOf(uploadProjectFile.fulfilled), (state) => {
        state.isLoading = false;
      })
      .addMatcher(
        isAnyOf(
          getProjects.pending,

          createResonanceProject.pending,
          createScoutProject.pending,
          createId8Project.pending,
          createGenieGoProject.pending,
          createGenieProject.pending,
          createActiv8Project.pending,

          uploadProjectFile.pending,
        ),
        (state) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getProjects.rejected,

          createResonanceProject.rejected,
          createScoutProject.rejected,
          createId8Project.rejected,
          createGenieGoProject.rejected,
          createGenieProject.rejected,
          createActiv8Project.rejected,

          uploadProjectFile.rejected,
        ),
        (state) => {
          state.isLoading = false;
        },
      );
  },
});

export const {
  changeTablePage,
  changeTableSize,
  changeTableSort,
  changeTableCreatedBy,
  changeTableProjectType,
  changeTableSearch,
  resetParams,
  resetEditProjectPage,
} = projectsSlice.actions;

export default projectsSlice.reducer;
